import { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Typography,
  Divider,
} from "@mui/material";
import type { ExportFormat, ExportSheetSelection, ExportOptions } from "@/types";

// ==================== TYPES ====================

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  onDownload: (options: ExportOptions) => void;
  /** Disable specific sheet options when no data is available */
  disabledSheets?: Partial<Record<keyof ExportSheetSelection, boolean>>;
}

interface SheetOption {
  key: keyof ExportSheetSelection;
  label: string;
}

// ==================== CONSTANTS ====================

const SHEET_OPTIONS: SheetOption[] = [
  { key: "summary", label: "Summary" },
  { key: "claimsDetail", label: "Claims Detail" },
  { key: "watchList", label: "Watch List" },
  { key: "reimbursement", label: "Reimbursements" },
  { key: "tasks", label: "Tasks" },
];

const DEFAULT_SHEETS: ExportSheetSelection = {
  summary: true,
  claimsDetail: true,
  watchList: true,
  reimbursement: true,
  tasks: true,
};

// ==================== COMPONENT ====================

export const ExportDialog = ({ open, onClose, onDownload, disabledSheets = {} }: ExportDialogProps) => {
  const [format, setFormat] = useState<ExportFormat>("excel");
  const [sheets, setSheets] = useState<ExportSheetSelection>(DEFAULT_SHEETS);

  const hasSelection = useMemo(
    () => SHEET_OPTIONS.some(({ key }) => sheets[key] && !disabledSheets[key]),
    [sheets, disabledSheets]
  );

  const handleSheetToggle = (key: keyof ExportSheetSelection) => {
    setSheets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDownload = () => {
    onDownload({ format, sheets });
    onClose();
  };

  /** Reset to defaults when dialog opens */
  const handleEnter = () => {
    setFormat("excel");
    setSheets(DEFAULT_SHEETS);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle variant="h3">Export Claims Summary</DialogTitle>

      <DialogContent>
        {/* Format selection */}
        <FormControl component="fieldset" sx={{ mb: 2.5 }}>
          <FormLabel component="legend" sx={{ fontSize: "0.875rem", fontWeight: 600, mb: 0.5 }}>
            Format
          </FormLabel>
          <RadioGroup value={format} onChange={(e) => setFormat(e.target.value as ExportFormat)}>
            <FormControlLabel
              value="excel"
              control={<Radio size="small" />}
              label={<Typography variant="body2">Excel (.xlsx)</Typography>}
            />
            <FormControlLabel
              value="pdf"
              disabled
              control={<Radio size="small" />}
              label={
                <Typography variant="body2" color="text.disabled">
                  PDF — Coming soon
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>

        <Divider sx={{ mb: 2 }} />

        {/* Sheet selection */}
        <FormControl component="fieldset">
          <FormLabel component="legend" sx={{ fontSize: "0.875rem", fontWeight: 600, mb: 0.5 }}>
            Include in export
          </FormLabel>
          <FormGroup>
            {SHEET_OPTIONS.map(({ key, label }) => {
              const isDisabled = !!disabledSheets[key];

              return (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      size="small"
                      checked={sheets[key] && !isDisabled}
                      onChange={() => handleSheetToggle(key)}
                      disabled={isDisabled}
                    />
                  }
                  label={
                    <Typography variant="body2" color={isDisabled ? "text.disabled" : undefined}>
                      {label}
                      {isDisabled && " (no data)"}
                    </Typography>
                  }
                />
              );
            })}
          </FormGroup>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleDownload} variant="contained" disabled={!hasSelection}>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};
